import { LogOut, Ban } from 'lucide-react';
import { isIndefiniteBan } from '@app/common';

import { Button } from '../core/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../core/Dialog';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../context/LanguageContext';
import { LOGIN_PATH } from '../../constants/routes';
import { formatDateTime } from '../admin/AdminShared';

/**
 * Blocks the app once the API has refused the account itself.
 *
 * Deliberately not the session-expiry dialog: that one tells the user to sign in
 * again, which here would send them at a login Supabase is already refusing. This
 * says why they were cut off and when — or that there is no when — so the only
 * remaining question is who to appeal to.
 */
export function AccountSuspendedDialog() {
  const { suspension } = useAuth();
  const { t } = useTranslation();

  if (!suspension) return null;

  // No expiry, or one so far out it was an "indefinite" ban rather than a date.
  const indefinite = !suspension.bannedUntil || isIndefiniteBan(suspension.bannedUntil);

  return (
    <Dialog open>
      <DialogContent
        hideClose
        className="max-w-md"
        // No escape hatch, for the same reason as the expiry dialog: dismissing would
        // leave the user on a dashboard that can no longer reach the API.
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-500/15">
              <Ban className="h-5 w-5 text-rose-400" />
            </div>
            <DialogTitle>{t('suspended.title')}</DialogTitle>
          </div>
          <DialogDescription className="pt-3 text-slate-400">
            {indefinite ? t('suspended.descriptionIndefinite') : t('suspended.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-3 rounded-lg border border-slate-700 bg-slate-900/60 p-4">
          {suspension.reason && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {t('suspended.reasonLabel')}
              </p>
              <p className="mt-1 text-sm text-slate-200">{suspension.reason}</p>
            </div>
          )}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              {t('suspended.untilLabel')}
            </p>
            <p className="mt-1 text-sm text-slate-200">
              {indefinite ? t('suspended.indefinite') : formatDateTime(suspension.bannedUntil)}
            </p>
          </div>
        </div>

        <Button
          className="mt-6 w-full"
          // A full navigation rather than a router push, matching the expiry dialog: it
          // discards every piece of state still held from the dead session.
          onClick={() => window.location.replace(LOGIN_PATH)}
        >
          <LogOut className="h-4 w-4" />
          {t('suspended.logout')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
